import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { Store, Mail, Facebook, Instagram, Twitter, BookOpen, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useSettings } from '@/hooks/useProducts';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import QuickEditInline from '@/components/quickedit/QuickEditInline';
import api from '@/lib/api';
import { SITE_SHORT_NAME, IS_LODGING, IS_SOCIAL_CASINO } from '@/config/site';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAuthStore } from '@/stores/auth-store';
import type { FooterVariant } from '@/looks';
import { useSiteSettingMutation } from '@/hooks/useSiteSettingMutation';

function resolveIcon(name: string | undefined, className: string) {
  const Icon = name ? ((LucideIcons as any)[name] as React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }> | undefined) : undefined;
  const Resolved = Icon ?? Sparkles;
  return <Resolved className={className} aria-hidden />;
}

interface FooterLink {
  label: string;
  href?: string;
  axissOpen?: boolean;
  axissView?: 'home' | 'chat';
}


function FooterLinkItem({ link }: { link: FooterLink }) {
  const linkClass = "text-sage-300 hover:text-white transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded";

  if (link.axissOpen) {
    return (
      <button data-axiss-open={link.axissView || 'home'} className={linkClass}>
        {link.label}
      </button>
    );
  }

  return (
    <Link to={link.href || '#'} className={linkClass}>
      {link.label}
    </Link>
  );
}

interface FooterProps {
  /**
   * Render the stripped-down "minimal chrome" variant: a single compliance
   * row with responsible-play + age + copyright. No link columns, no
   * newsletter form. Seeded for social_casino tenants by the wizard.
   *
   * Equivalent to passing `variant="minimal"`; kept for backwards compatibility.
   */
  minimal?: boolean;
  /**
   * Layout variant from the tenant's selected Look (theme_config.look).
   * - 'multi_column' (default): 4-column grid (Brand / Shop / Support / Newsletter)
   * - 'compact':                3-column grid, no newsletter column, no value-props bar
   * - 'big_cta':                full-width newsletter banner above 3-column link grid
   * - 'minimal':                same as the `minimal` prop above
   */
  variant?: FooterVariant;
}

export default function Footer({ minimal: minimalProp = false, variant = 'multi_column' }: FooterProps = {}) {
  const minimal = minimalProp || variant === 'minimal';
  const isCompact = variant === 'compact';
  const isBigCta = variant === 'big_cta';
  const { t } = useTranslation(['footer', 'nav', 'common']);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState('');
  const { data: settingsData } = useSettings();
  const { isActive } = useQuickEdit();
  // One save path for every quick-edit field on this component: the hook
  // owns the cache invalidation, so it cannot be forgotten at a call site.
  const settingMutation = useSiteSettingMutation();
  const saveSetting = (settings: Record<string, unknown>) =>
    settingMutation.mutateAsync(settings);
  const { isEnabled } = useFeatureFlags();
  const location = useLocation();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');
  const settings = settingsData?.settings;
  const user = useAuthStore((s) => s.user);
  // The homepage renders value props as its own section, so skip the footer
  // bar there to avoid showing the same four cards twice. Also skip when
  // the tenant has enabled `hide_value_props_when_authed` and the visitor
  // is signed in — used by SaaS sites whose authed users have the app
  // surface and don't need marketing differentiators on every page.
  const isHome = location.pathname === '/';
  const hideValuePropsForAuthed = !!user && !!settings?.hide_value_props_when_authed;
  // Compact footer omits the value-props bar; the link grid is the whole story.
  const valuePropositions = isHome || hideValuePropsForAuthed || isCompact ? undefined : settings?.value_propositions;
  const footerAbout = settings?.footer_about_text;
  const storeName = settings?.store_name || SITE_SHORT_NAME;

  /**
   * Legal operating entity, shown beneath the copyright when the tenant sets
   * `legal_entity` in site_settings.
   *
   * Carrier vetting (toll-free verification, A2P 10DLC) checks that the
   * business name on the filing actually appears on the site it names. A brand
   * trading under an assumed name has nothing tying it to the LLC that filed,
   * so a reviewer landing on the site sees no connection and the filing reads
   * as unverifiable. Wording is deliberately generic: "operated by" is true
   * both for a DBA brand and for a site whose brand IS the entity.
   *
   * Optional — sites that have no separate operating entity leave it unset and
   * the line is omitted entirely.
   */
  const legalEntity =
    typeof settings?.legal_entity === 'string' ? settings.legal_entity.trim() : '';
  const legalEntityLine = legalEntity ? ` Operated by ${legalEntity}.` : '';

  /**
   * Support phone, when the tenant has one.
   *
   * Written by the site wizard when a customer buys the phone add-on, and
   * settable by hand in admin. `store_phone` has been in the public settings
   * allowlist all along but was rendered nowhere — so a tenant who set it saw
   * it go precisely nowhere, and a tenant who bought a support line had no
   * place on their own site for it to appear.
   *
   * Rendered as tel: because the overwhelming majority of visitors who want a
   * phone number are holding a phone.
   */
  const storePhone =
    typeof settings?.store_phone === 'string' ? settings.store_phone.trim() : '';
  // tel: wants no spaces, parens or dashes; the visible text keeps them.
  //
  // A bare ten-digit result is a US number written the way Americans write one,
  // and `tel:8559424019` leaves the country code to whatever the dialer assumes
  // — which is wrong for anyone roaming or on a non-US carrier. Anything that
  // already carries a `+`, or is any other length, is left exactly as written:
  // guessing a country for an unfamiliar number would be worse than not.
  const storePhoneDigits = storePhone.replace(/[^\d+]/g, '');
  const storePhoneHref = /^\d{10}$/.test(storePhoneDigits)
    ? `+1${storePhoneDigits}`
    : storePhoneDigits;

  // Footer's brand column is narrow (~1/4 of viewport on lg). About text that
  // runs more than a couple sentences makes the column tower over the others
  // and looks lopsided. Truncate at a word boundary and link to /page/about
  // when there's significantly more text — keeps the layout tight without
  // hiding content from anyone who wants to read it.
  const FOOTER_ABOUT_MAX = 140;
  function truncateAtWord(text: string, max: number): string {
    if (text.length <= max) return text;
    const slice = text.slice(0, max);
    const lastSpace = slice.lastIndexOf(' ');
    return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd() + '…';
  }
  const footerAboutFull =
    footerAbout || `Welcome to ${storeName}. ${showEcommerce ? 'Quality products and great service.' : 'Start your learning journey today.'}`;
  const footerAboutNeedsTruncation = !isActive && footerAboutFull.length > FOOTER_ABOUT_MAX;
  const footerAboutDisplay = footerAboutNeedsTruncation ? truncateAtWord(footerAboutFull, FOOTER_ABOUT_MAX) : footerAboutFull;

  const defaultShopLinks: FooterLink[] = IS_LODGING
    ? [
        { label: t('nav:stays', { defaultValue: 'All Properties' }), href: '/properties' },
        { label: t('nav:findReservation', { defaultValue: 'Find My Reservation' }), href: '/reservations' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : showEcommerce
    ? [
        { label: t('footer:shop.allProducts'), href: '/shop' },
        { label: t('footer:shop.newArrivals'), href: '/shop?sort=newest' },
        { label: t('footer:shop.bundles'), href: '/bundles' },
        { label: t('footer:shop.giftCards'), href: '/gift-cards' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : showCourses
    ? [
        { label: t('footer:shop.courses'), href: '/courses' },
        { label: t('footer:shop.pricing'), href: '/pricing' },
        { label: t('nav:blog'), href: '/blog' },
      ]
    : [{ label: t('nav:blog'), href: '/blog' }];

  const defaultSupportLinks: FooterLink[] = [
    { label: t('nav:myAccount'), href: '/account' },
    { label: t('footer:company.about'), href: '/page/about' },
    { label: t('footer:support.liveChat'), axissOpen: true, axissView: 'chat' },
  ];

  // Settings come from /api/settings/public OR a stale localStorage cache that
  // is shared across tenants (key 'rumio-settings-cache'). A bad shape there used
  // to crash render with `S.map is not a function`. Guard against non-arrays.
  const shopLinks: FooterLink[] = Array.isArray(settings?.footer_shop_links)
    ? settings.footer_shop_links
    : defaultShopLinks;
  const shopSectionTitle = showEcommerce
    ? t('footer:shop.heading')
    : showCourses
      ? t('footer:shop.learn')
      : t('footer:shop.explore');
  const supportLinks: FooterLink[] = Array.isArray(settings?.footer_support_links)
    ? settings.footer_support_links
    : defaultSupportLinks;
  const newsletterTitle = settings?.footer_newsletter_title || t('footer:newsletter.heading');
  const newsletterSubtitle = settings?.footer_newsletter_subtitle || t('footer:newsletter.description');

  // Social links from individual setting keys
  const facebookUrl = settings?.facebook_url;
  const instagramUrl = settings?.instagram_url;
  const twitterUrl = settings?.twitter_url;
  const hasSocials = facebookUrl || instagramUrl || twitterUrl;

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError('');
    try {
      await api.post('/newsletter/subscribe', { email });
      setSubscribed(true);
      setEmail('');
    } catch {
      setError(t('footer:newsletter.errorGeneric'));
    }
  };

  if (minimal) {
    const storeName = settings?.store_name || SITE_SHORT_NAME;
    const minAge: number = typeof settings?.min_age === 'number' ? settings.min_age : 18;
    const year = new Date().getFullYear();
    return (
      <footer role="contentinfo" className="bg-surface-muted border-t border-line" data-minimal-chrome>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-ink-soft">
          <p>
            © {year} {storeName}.
            {legalEntityLine}
            {storePhone && (
              <>
                {' '}
                <a
                  href={`tel:${storePhoneHref}`}
                  className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                >
                  {storePhone}
                </a>
              </>
            )}
            {/* Play-for-fun / virtual-coins disclaimer is social-casino-specific.
                The minimal footer is a *layout* choice (any tenant can pick a Look
                that uses it), so gate the casino copy on IS_SOCIAL_CASINO — else a
                non-casino site (e.g. rumio.world itself) shows gambling text. */}
            {IS_SOCIAL_CASINO &&
              ' Play-for-fun only. Virtual coins are not redeemable for cash.'}
          </p>
          <div className="flex items-center gap-4">
            {/* Age badge + responsible-play link are also social-casino chrome. */}
            {IS_SOCIAL_CASINO && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ink text-surface text-[10px] font-bold">
                {minAge}+
              </span>
            )}
            {IS_SOCIAL_CASINO && (
              <Link to="/page/responsible-play" className="hover:text-ink">
                {t('footer:responsiblePlay', { defaultValue: 'Responsible play' })}
              </Link>
            )}
            {/* Slugs must be the full ones — every tenant seeds `terms-of-service`
                and `privacy-policy`, and none has a page at `terms` or `privacy`,
                so the short forms 404. The multi-column footer below always used
                the full slugs; only this variant was wrong. */}
            <Link to="/page/terms-of-service" className="hover:text-ink">
              {t('footer:terms', { defaultValue: 'Terms' })}
            </Link>
            <Link to="/page/privacy-policy" className="hover:text-ink">
              {t('footer:privacy', { defaultValue: 'Privacy' })}
            </Link>
            <Link to="/copyright" className="hover:text-ink">
              {t('footer:legal.copyright', { defaultValue: 'Copyright' })}
            </Link>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer role="contentinfo">
      {/* Value props bar */}
      {valuePropositions && Array.isArray(valuePropositions) && valuePropositions.length > 0 && (
        <div className="bg-linen border-t border-sage-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {valuePropositions.map((item: any, i: number) => (
                <li key={i} className="flex items-start gap-3 text-sage-800">
                  <div className="shrink-0 mt-0.5 text-sage-600">
                    {resolveIcon(item.icon, 'h-6 w-6')}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {isActive ? (
                        <QuickEditInline
                          value={item.title}
                          onSave={async (val) => {
                            const updated = [...valuePropositions];
                            updated[i] = { ...updated[i], title: val };
                            await saveSetting({ value_propositions: updated });
                          }}
                          label={`Footer value proposition ${i + 1} title`}
                          as="span"
                          className="font-semibold text-sm"
                        />
                      ) : item.title}
                    </p>
                    <p className="text-xs text-sage-600 mt-0.5">
                      {isActive ? (
                        <QuickEditInline
                          value={item.description}
                          onSave={async (val) => {
                            const updated = [...valuePropositions];
                            updated[i] = { ...updated[i], description: val };
                            await saveSetting({ value_propositions: updated });
                          }}
                          label={`Footer value proposition ${i + 1} description`}
                          as="span"
                          className="text-xs text-sage-600"
                        />
                      ) : item.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Main footer — a deliberately-dark band. `surface-fixed-dark` pins the
          sage scale to its light values so this block (and its sage-800 inputs /
          newsletter panel) stays the tenant's dark-brand color in dark mode too,
          instead of inverting bright. */}
      <div className="surface-fixed-dark bg-sage-900 text-sage-100">
        {/* big_cta: full-width newsletter banner above the link grid */}
        {isBigCta && !subscribed && (
          <div className="border-b border-sage-800">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                {newsletterTitle}
              </h2>
              <p className="text-sage-300 mb-5">{newsletterSubtitle}</p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                <label htmlFor="footer-cta-email" className="sr-only">{t('footer:newsletter.emailLabel')}</label>
                <input
                  id="footer-cta-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer:newsletter.emailPlaceholder')}
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-sage-800 border border-sage-700 text-white placeholder:text-sage-500 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-sage-500 hover:bg-sage-400 text-white rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  {t('footer:newsletter.subscribe')}
                </button>
              </form>
              {error && <p className="text-xs text-red-300 mt-2" role="alert">{error}</p>}
            </div>
          </div>
        )}
        <div className={cn('max-w-7xl mx-auto px-4 sm:px-6 lg:px-8', isCompact ? 'py-8' : 'py-12')}>
          <div className={cn(
            'grid grid-cols-1 sm:grid-cols-2 gap-8',
            // big_cta and compact: 3-col grid (Brand spans 1, Shop, Support — no Newsletter column)
            // multi_column: 4-col grid (Brand, Shop, Support, Newsletter)
            (isCompact || isBigCta) ? 'lg:grid-cols-3' : 'lg:grid-cols-4',
          )}>

            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                {showCourses && !showEcommerce
                  ? <BookOpen className="h-6 w-6 text-sage-400" aria-hidden="true" />
                  : <Store className="h-6 w-6 text-sage-400" aria-hidden="true" />}
                <span className="font-display text-lg font-bold text-white">
                  {isActive ? (
                    <QuickEditInline
                      value={storeName}
                      onSave={async (val) => {
                        await saveSetting({ store_name: val });
                      }}
                      label="Footer store name"
                      as="span"
                      className="font-display text-lg font-bold text-white"
                    />
                  ) : storeName}
                </span>
              </div>
              <p className="text-sm text-sage-300 mb-4">
                {isActive ? (
                  <QuickEditInline
                    value={footerAboutFull}
                    onSave={async (val) => {
                      await saveSetting({ footer_about_text: val });
                    }}
                    label="Footer about text"
                    as="span"
                    className="text-sm text-sage-300"
                    multiline
                  />
                ) : (
                  <>
                    {footerAboutDisplay}
                    {footerAboutNeedsTruncation && (
                      <>
                        {' '}
                        <Link
                          to="/page/about"
                          className="text-sage-200 underline underline-offset-2 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                        >
                          {t('footer:readMore', { defaultValue: 'Read more' })}
                        </Link>
                      </>
                    )}
                  </>
                )}
              </p>
              {hasSocials && (
                <div className="flex gap-3">
                  {facebookUrl && (
                    <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400" aria-label={t('footer:social.facebook')}>
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {instagramUrl && (
                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400" aria-label={t('footer:social.instagram')}>
                      <Instagram className="h-5 w-5" />
                    </a>
                  )}
                  {twitterUrl && (
                    <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400" aria-label={t('footer:social.twitter')}>
                      <Twitter className="h-5 w-5" />
                    </a>
                  )}
                </div>
              )}
              {!hasSocials && (
                <div className="flex gap-3">
                  <span className="text-sage-400 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg" aria-label={t('footer:social.facebook')}>
                    <Facebook className="h-5 w-5" />
                  </span>
                  <span className="text-sage-400 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg" aria-label={t('footer:social.instagram')}>
                    <Instagram className="h-5 w-5" />
                  </span>
                  <span className="text-sage-400 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg" aria-label={t('footer:social.twitter')}>
                    <Twitter className="h-5 w-5" />
                  </span>
                </div>
              )}
            </div>

            {/* Shop */}
            <div>
              <h2 className="font-semibold text-white mb-3 text-base">{shopSectionTitle}</h2>
              <ul className="space-y-2 text-sm">
                {shopLinks.map((link, i) => (
                  <li key={i}><FooterLinkItem link={link} /></li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h2 className="font-semibold text-white mb-3 text-base">{t('footer:support.heading')}</h2>
              <ul className="space-y-2 text-sm">
                {supportLinks.map((link, i) => (
                  <li key={i}><FooterLinkItem link={link} /></li>
                ))}
              </ul>
            </div>

            {/* Newsletter — rendered only in multi_column. big_cta hoists it
                to a full-width banner above the grid; compact drops it. */}
            {!isCompact && !isBigCta && (
              <div>
                <div className="bg-sage-800/50 rounded-lg p-6">
                  <h2 className="font-semibold text-white mb-2 text-base">
                    {isActive ? (
                      <QuickEditInline
                        value={newsletterTitle}
                        onSave={async (val) => {
                          await saveSetting({ footer_newsletter_title: val });
                        }}
                        label="Newsletter title"
                        as="span"
                        className="font-semibold text-white"
                      />
                    ) : newsletterTitle}
                  </h2>
                  <p className="text-sm text-sage-300 mb-3">
                    {isActive ? (
                      <QuickEditInline
                        value={newsletterSubtitle}
                        onSave={async (val) => {
                          await saveSetting({ footer_newsletter_subtitle: val });
                        }}
                        label="Newsletter subtitle"
                        as="span"
                        className="text-sm text-sage-300"
                      />
                    ) : newsletterSubtitle}
                  </p>
                  {subscribed ? (
                    <p className="text-sm text-sage-300 font-medium" role="status">{t('footer:newsletter.subscribed')}</p>
                  ) : (
                    <form onSubmit={handleSubscribe} className="space-y-2">
                      <label htmlFor="footer-email" className="sr-only">{t('footer:newsletter.emailLabel')}</label>
                      <input
                        id="footer-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('footer:newsletter.emailPlaceholder')}
                        required
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'footer-email-error' : undefined}
                        className="w-full px-3 py-2 rounded-lg bg-sage-800 border border-sage-700 text-white placeholder:text-sage-500 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
                      />
                      <button
                        type="submit"
                        className={cn(
                          'w-full px-3 py-2 bg-sage-600 hover:bg-sage-500 text-white rounded-lg text-sm font-medium transition-colors',
                          'min-h-[44px] flex items-center justify-center gap-2',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400'
                        )}
                      >
                        <Mail className="h-4 w-4" aria-hidden="true" />
                        {t('footer:newsletter.subscribe')}
                      </button>
                      {error && (
                        <p id="footer-email-error" className="text-xs text-red-300" role="alert">
                          {error}
                        </p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 pt-8 border-t border-sage-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-sage-400">
            <p>
              {t('footer:copyright', { year: new Date().getFullYear(), name: storeName })}
              {legalEntityLine}
              {storePhone && (
                <>
                  {' '}
                  <a
                    href={`tel:${storePhoneHref}`}
                    className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
                  >
                    {storePhone}
                  </a>
                </>
              )}
            </p>
            <div className="flex gap-4">
              <Link to="/page/privacy-policy" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">{t('footer:legal.privacy')}</Link>
              <Link to="/page/terms-of-service" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">{t('footer:legal.terms')}</Link>
              {/* §512(c)(2) wants the designated agent reachable from the public
                  site, not just on file with the Copyright Office. Both footer
                  variants carry this link — don't drop it in a tidy-up. */}
              <Link to="/copyright" className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">{t('footer:legal.copyright', { defaultValue: 'Copyright' })}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
